import React, { useEffect, useState } from 'react';
import { getAllLeaves, getLeavesByStaff, processLeave, applyLeave } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS = {
  PENDING:  { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', border: '#fde68a' },
  APPROVED: { bg: '#d1fae5', color: '#065f46', dot: '#10b981', border: '#6ee7b7' },
  REJECTED: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', border: '#fca5a5' },
};

const LEAVE_TYPES = ['CASUAL','SICK','EARNED','MATERNITY','PATERNITY','SPECIAL'];
const LEAVE_CFG = {
  CASUAL:    { bg: '#dbeafe', color: '#1d4ed8' },
  SICK:      { bg: '#fce7f3', color: '#be185d' },
  EARNED:    { bg: '#d1fae5', color: '#065f46' },
  MATERNITY: { bg: '#ede9fe', color: '#6d28d9' },
  PATERNITY: { bg: '#cffafe', color: '#0e7490' },
  SPECIAL:   { bg: '#fef3c7', color: '#92400e' },
};

export default function Leave() {
  const { isAdmin, user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [tab, setTab] = useState('ALL');
  const [applyModal, setApplyModal] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [form, setForm] = useState({ leaveType: 'CASUAL', fromDate: '', toDate: '', reason: '' });
  const [reviewRemark, setReviewRemark] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    const req = isAdmin() ? getAllLeaves() : getLeavesByStaff(user?.staffId);
    req.then(r => { setLeaves(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.fromDate || !form.toDate) { toast.error('Please select dates'); return; }
    setSubmitting(true);
    try {
      const from = new Date(form.fromDate), to = new Date(form.toDate);
      const days = Math.max(1, Math.round((to - from) / 86400000) + 1);
      await applyLeave({ ...form, days, staff: { id: user?.staffId } });
      toast.success('Leave applied!');
      setApplyModal(false);
      setForm({ leaveType: 'CASUAL', fromDate: '', toDate: '', reason: '' });
      load();
    } catch { toast.error('Failed to apply leave'); }
    finally { setSubmitting(false); }
  };

  const handleReview = async (action) => {
    try {
      await processLeave(reviewModal.id, { action, remarks: reviewRemark });
      toast.success(`Leave ${action === 'APPROVE' ? 'approved' : 'rejected'}!`);
      setReviewModal(null); setReviewRemark(''); load();
    } catch { toast.error('Action failed'); }
  };

  const daysBetween = (a, b) => !a || !b ? 0 : Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);

  const counts = { ALL: leaves.length, PENDING: 0, APPROVED: 0, REJECTED: 0 };
  leaves.forEach(l => { if (counts[l.status] !== undefined) counts[l.status]++; });
  const filtered = tab === 'ALL' ? leaves : leaves.filter(l => l.status === tab);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Leave Management</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
            {isAdmin() ? 'Review and manage all leave requests' : 'View your leaves and apply for new ones'}
          </div>
        </div>
        <button onClick={() => setApplyModal(true)} style={{
          padding: '10px 22px', background: 'linear-gradient(135deg, #1a237e, #1565c0)',
          color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer',
          fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)',
        }}>+ Apply Leave</button>
      </div>

      {/* Status Filter Cards */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        {[
          { key: 'ALL',      label: 'All',      icon: '📋', color: '#3b82f6', bg: '#eff6ff' },
          { key: 'PENDING',  label: 'Pending',  icon: '⏳', color: '#f59e0b', bg: '#fffbeb' },
          { key: 'APPROVED', label: 'Approved', icon: '✅', color: '#10b981', bg: '#f0fdf4' },
          { key: 'REJECTED', label: 'Rejected', icon: '❌', color: '#ef4444', bg: '#fff1f2' },
        ].map(s => (
          <div key={s.key} onClick={() => setTab(s.key)} style={{
            flex: 1, minWidth: 120, background: tab === s.key ? s.color : '#fff',
            borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
            boxShadow: tab === s.key ? `0 6px 20px ${s.color}40` : '0 2px 10px rgba(0,0,0,0.05)',
            border: `2px solid ${tab === s.key ? s.color : '#f1f5f9'}`,
            transition: 'all 0.2s',
          }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: tab === s.key ? '#fff' : s.color }}>{counts[s.key]}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: tab === s.key ? 'rgba(255,255,255,0.8)' : '#64748b' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Leave Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>⏳</div>Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, background: '#fff', borderRadius: 16, color: '#94a3b8', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>No leave requests</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Apply for a leave using the button above</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(lv => {
            const st = STATUS[lv.status] || STATUS.PENDING;
            const lc = LEAVE_CFG[lv.leaveType] || LEAVE_CFG.CASUAL;
            const days = daysBetween(lv.fromDate, lv.toDate);
            return (
              <div key={lv.id} style={{
                background: '#fff', borderRadius: 14, padding: '18px 22px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex',
                alignItems: 'center', gap: 18, borderLeft: `4px solid ${st.dot}`,
                transition: 'box-shadow 0.15s',
              }}>
                {/* Type + Days */}
                <div style={{ background: lc.bg, borderRadius: 12, padding: '12px 16px', textAlign: 'center', minWidth: 72, flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: lc.color, letterSpacing: 0.5 }}>{lv.leaveType}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: lc.color, lineHeight: 1.2, marginTop: 3 }}>{days}</div>
                  <div style={{ fontSize: 9, color: lc.color, opacity: 0.7 }}>DAYS</div>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{lv.staff?.name || 'Unknown'}</span>
                    <span style={{ background: st.bg, color: st.color, padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: `1px solid ${st.border}` }}>{lv.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>
                    📅 {lv.fromDate} → {lv.toDate}
                    <span style={{ marginLeft: 14, color: '#94a3b8' }}>
                      Applied: {lv.createdAt ? new Date(lv.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    💬 {lv.reason}
                  </div>
                  {lv.remarks && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Remarks: {lv.remarks}</div>
                  )}
                </div>

                {/* Admin Review */}
                {isAdmin() && lv.status === 'PENDING' && (
                  <button onClick={() => { setReviewModal(lv); setReviewRemark(''); }} style={{
                    padding: '8px 18px', borderRadius: 10, background: '#eff6ff',
                    color: '#2563eb', border: '1px solid #bfdbfe', cursor: 'pointer',
                    fontWeight: 700, fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap',
                  }}>Review →</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Apply Modal */}
      {applyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 520, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>Apply for Leave</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 3 }}>Fill in the details below</div>
              </div>
              <button onClick={() => setApplyModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <form onSubmit={handleApply} style={{ padding: 28 }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>Leave Type</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {LEAVE_TYPES.map(t => {
                    const lc = LEAVE_CFG[t] || LEAVE_CFG.CASUAL;
                    return (
                      <button type="button" key={t} onClick={() => setForm({ ...form, leaveType: t })} style={{
                        padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: form.leaveType === t ? lc.color : lc.bg,
                        color: form.leaveType === t ? '#fff' : lc.color,
                        border: `2px solid ${form.leaveType === t ? lc.color : 'transparent'}`,
                        transition: 'all 0.15s',
                      }}>{t}</button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                {['fromDate','toDate'].map(field => (
                  <div key={field}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>{field === 'fromDate' ? 'From Date' : 'To Date'}</div>
                    <input type="date" value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })} required
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>

              {form.fromDate && form.toDate && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>
                  📅 {daysBetween(form.fromDate, form.toDate)} day(s) of leave requested
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Reason *</div>
                <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required rows={3}
                  placeholder="Please provide a reason for your leave..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setApplyModal(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{ flex: 2, padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Submitting...' : 'Submit Leave Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 460, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>Review Request</div>
              <button onClick={() => setReviewModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 28 }}>
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.5 }}>STAFF</div><div style={{ fontWeight: 700, color: '#0f172a', marginTop: 3 }}>{reviewModal.staff?.name}</div></div>
                  <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.5 }}>TYPE</div><div style={{ fontWeight: 700, color: '#1d4ed8', marginTop: 3 }}>{reviewModal.leaveType}</div></div>
                  <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.5 }}>FROM</div><div style={{ fontWeight: 600, marginTop: 3 }}>{reviewModal.fromDate}</div></div>
                  <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.5 }}>TO</div><div style={{ fontWeight: 600, marginTop: 3 }}>{reviewModal.toDate}</div></div>
                </div>
                <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>REASON</div><div style={{ fontSize: 13, color: '#475569' }}>{reviewModal.reason}</div></div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Remarks (optional)</div>
                <textarea value={reviewRemark} onChange={e => setReviewRemark(e.target.value)} rows={2} placeholder="Add comments..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setReviewModal(null)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button onClick={() => handleReview('REJECT')} style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', cursor: 'pointer', fontWeight: 700 }}>✗ Reject</button>
                <button onClick={() => handleReview('APPROVE')} style={{ flex: 1, padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}>✓ Approve</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
